import { useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, XCircle, Loader2 } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const badge = (s) => ({ POSTED: "border-emerald-200 bg-emerald-50 text-emerald-700", CANCELLED: "border-stone-200 bg-stone-100 text-stone-500" }[s] || "border-stone-200 bg-stone-50 text-stone-600");

export default function CreditNotes() {
  const { data: cnRaw, refetch } = useResource("/credit-notes");
  const notes = Array.isArray(cnRaw) ? cnRaw : [];
  const { data: billsRaw } = useResource("/sales-bills");
  const bills = (Array.isArray(billsRaw) ? billsRaw : []).filter((b) => b.status !== "CANCELLED");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ invoiceId: "", taxableValue: "", gstPct: "18", reason: "", date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);

  const total = (() => { const t = Number(form.taxableValue || 0); return t + (t * Number(form.gstPct || 0) / 100); })();

  const create = async () => {
    if (!form.invoiceId) return toast.error("Select an invoice");
    if (!(Number(form.taxableValue) > 0)) return toast.error("Enter a taxable value");
    setBusy(true);
    try {
      const { data } = await api.post("/credit-notes", { invoiceId: form.invoiceId, taxableValue: Number(form.taxableValue), gstPct: Number(form.gstPct || 0), reason: form.reason, date: form.date });
      toast.success(`Credit Note ${data.creditNoteNo} posted`);
      setOpen(false); setForm({ invoiceId: "", taxableValue: "", gstPct: "18", reason: "", date: new Date().toISOString().slice(0, 10) }); refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); } finally { setBusy(false); }
  };

  const cancel = async (c) => { const reason = window.prompt("Cancellation reason?") || ""; try { await api.post(`/credit-notes/${c.id}/cancel`, { reason }); toast.success("Credit note cancelled"); refetch(); } catch (e) { toast.error(apiErrorMessage(e, "Cancel failed")); } };

  return (
    <div className="space-y-6">
      <PageHeader title="Credit Notes" subtitle="Standalone or return-linked · reduces buyer receivable · original invoice stays immutable">
        <Button data-testid="cn-new-btn" className="gap-2 bg-amber-700 hover:bg-amber-800" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Credit Note</Button>
      </PageHeader>

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="cn-list">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500"><tr>{["CN #", "Date", "Invoice", "Buyer", "Return", "Reason", "Taxable", "GST", "Total", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-stone-100">
            {notes.length === 0 ? <tr><td colSpan={11} className="px-3 py-6 text-center text-stone-400">No credit notes yet.</td></tr> :
              notes.map((c) => (
                <tr key={c.id} data-testid={`cn-row-${c.creditNoteNo}`} className="hover:bg-stone-50/60">
                  <td className="px-3 py-2 font-mono text-xs">{c.creditNoteNo}</td><td className="px-3 py-2">{c.date}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.invoiceNo}</td><td className="px-3 py-2">{c.buyerName}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-stone-500">{c.returnNo || "—"}</td>
                  <td className="px-3 py-2 text-xs text-stone-500">{c.reason}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(c.taxableValue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(c.gstAmount)}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{money(c.totalCredit)}</td>
                  <td className="px-3 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge(c.status)}`}>{c.status}</span></td>
                  <td className="px-3 py-1.5">{c.status === "POSTED" && <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-500" data-testid={`cn-cancel-${c.creditNoteNo}`} onClick={() => cancel(c)}><XCircle className="h-4 w-4" /></Button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="cn-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-amber-700" /> New Credit Note</DialogTitle>
            <DialogDescription className="text-xs">Standalone credit against an invoice. (Return-linked credit notes are raised from Sales Returns.)</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Invoice</Label>
              <select data-testid="cn-invoice" value={form.invoiceId} onChange={(e) => setForm({ ...form, invoiceId: e.target.value })} className="h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm">
                <option value="">Select invoice…</option>
                {bills.map((b) => <option key={b.id} value={b.id}>{b.billNo || b.externalInvoiceNumber} · {b.buyerName} · {money(b.grandTotal)}</option>)}
              </select></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Taxable Value</Label><Input type="number" data-testid="cn-taxable" value={form.taxableValue} onChange={(e) => setForm({ ...form, taxableValue: e.target.value })} className="h-9" /></div>
              <div><Label className="text-xs">GST %</Label><Input type="number" data-testid="cn-gst" value={form.gstPct} onChange={(e) => setForm({ ...form, gstPct: e.target.value })} className="h-9" /></div>
              <div><Label className="text-xs">Date</Label><Input type="date" data-testid="cn-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" /></div>
            </div>
            <div><Label className="text-xs">Reason</Label><Input data-testid="cn-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="h-9" placeholder="Discount / price adjustment / short supply…" /></div>
            <div className="text-sm text-stone-600">Total Credit: <b data-testid="cn-total" className="font-mono text-amber-700">{money(total)}</b></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="cn-create" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={create}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Post Credit Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
